import React from "react";
import { AppointmentDate } from "@utils/appointmentDate/index";
import StorageUtils from "@services/storage";
import { ui, state as STATE } from "../../../../../../../../interfaces/interfaces";
import { state } from './../../../../../../../../interfaces/interfaces';
import { Dayjs } from "dayjs";

export const getTimeRangeText = (appointmentTime: "am" | "pm" | "eve") => {
  switch (appointmentTime) {
    case "am":
      return "up to 12:00";
    case "pm":
      return "from 12:00 to 17:00";
    case "eve":
      return "after 17:00";
    default:
      return "";
  }
}

export type AppointmentTime = "am" | "pm" | "eve";

export type AppointmentRadios = {
  am: boolean;
  pm: boolean;
  eve: boolean;
}

export interface DateSelectorState {
  appointmentDate?: Date;
  appointmentTime: AppointmentTime;
  appointmentRadios: AppointmentRadios;
  emergency: boolean;
  emergencyOpen: boolean;
}

export enum ACTIONS {
  SET_APPOINTMENT_TIME,
  SET_APPOINTMENT_DATE,
  SET_APPOINTMENT_RADIOS,
  SET_EMERGENCY,
  SET_ALL
}

export interface SET_ALL {
  type: ACTIONS.SET_ALL;
  payload: any;
}

export interface SET_APPOINTMENT_TIME {
  type: ACTIONS.SET_APPOINTMENT_TIME;
  payload: AppointmentTime;
}

export interface SET_APPOINTMENT_DATE {
  type: ACTIONS.SET_APPOINTMENT_DATE;
  payload: Date;
}

export interface SET_APPOINTMENT_RADIOS {
  type: ACTIONS.SET_APPOINTMENT_RADIOS;
  payload: {
    am: boolean
    pm: boolean
    eve: boolean
  };
}

export interface SET_EMERGENCY {
  type: ACTIONS.SET_EMERGENCY;
  payload: boolean;
}

const appointmentDateFromStorage = () => {
  const appointmentDate = StorageUtils.getFromStorage({ type: "local", store: "ui", key: "AppointmentDate" })
  if (appointmentDate) {
    return new Date(appointmentDate);
  }
  return undefined;
};

const appointmentDateIsExpiringIn30Days = () => {
  const restrictionExpiresIn30Days = StorageUtils.getFromStorage({ type: "local", store: "state", key: "RestrictionExpiresIn30Days" })
  if (restrictionExpiresIn30Days > -1) {
    // new date + number of days
    return new Date(new Date().setDate(new Date().getDate() + restrictionExpiresIn30Days));
  }
  return undefined;
};

const appointmentTimeFromStorage = () => {
  const appointmentTime = StorageUtils.getFromStorage({ type: "local", store: "ui", key: "AppointmentTime" })
  if (appointmentTime) {
    return appointmentTime;
  }
  return "eve";
};

const appointmentRadiosFromStorage = (): DateSelectorState["appointmentRadios"] => {
  const appointmentRadios = StorageUtils.getFromStorage({ type: "local", store: "ui", key: "AppointmentRadios" })
  if (appointmentRadios) {
    return appointmentRadios;
  }
  return {
    am: false,
    pm: false,
    eve: true
  };
};

const emergencyFromStorage = () => {
  const emergency = StorageUtils.getFromStorage({ type: "local", store: "state", key: "Emergency" })
  if (emergency) {
    return emergency;
  }
  return false;
};

export type DateSelectorActions = SET_APPOINTMENT_TIME | SET_APPOINTMENT_DATE | SET_APPOINTMENT_RADIOS | SET_EMERGENCY | SET_ALL;

export const initialState = (): DateSelectorState => {
  let appointmentDate = (ui.AppointmentDate && new Date(ui.AppointmentDate)) || appointmentDateFromStorage();
  if(STATE.RestrictionExpiresIn30Days > -1) {
    const restrictionExpiresIn30Days = appointmentDateIsExpiringIn30Days();
    if(restrictionExpiresIn30Days && appointmentDate && appointmentDate < restrictionExpiresIn30Days) {
      appointmentDate = restrictionExpiresIn30Days;
    }
  }
  const emergency = STATE.Emergency || emergencyFromStorage();
  const appointmentTime = ui.AppointmentTime || appointmentTimeFromStorage();
  const appointmentRadios = ui.AppointmentRadios || appointmentRadiosFromStorage();
  const emergencyOpen = false;
  return {
    appointmentDate,
    appointmentTime,
    appointmentRadios,
    emergency,
    emergencyOpen
  }
};

export const reducer = (state: DateSelectorState, action: DateSelectorActions) => {
  if (!action) {
    return state;
  }
  switch (action.type) {
    case ACTIONS.SET_APPOINTMENT_DATE:
      return { ...state, appointmentDate: action.payload };
    case ACTIONS.SET_APPOINTMENT_TIME:
      return { ...state, appointmentTime: action.payload };
    case ACTIONS.SET_APPOINTMENT_RADIOS:
      return { ...state, appointmentRadios: action.payload };
    case ACTIONS.SET_EMERGENCY:
      return { ...state, emergency: action.payload };
    case ACTIONS.SET_ALL:
      return { appointmentTime: action.payload.appointmentTime, appointmentDate: action.payload.appointmentDate, appointmentRadios: action.payload.appointmentRadios, emergency: action.payload.emergency };

    default:
      return state;
  }
};

const setEarliestAppointmentTimeDateForToday = () => {
  const appointmentTime = AppointmentDate.getEarliestAppointmentTime(new Date());
  const appointmentDate = AppointmentDate.changeToValidTimeForDate(new Date(), appointmentTime);
  const appointmentRadios = AppointmentDate.getValidAmPmEveOptions(new Date());
  return { appointmentTime, appointmentDate, appointmentRadios, emergency: false }
};

const setAppointmentDateForToday = (appointmentTime: AppointmentTime) => {
  const appointmentDate = AppointmentDate.changeToValidTimeForDate(new Date(), appointmentTime);
  const appointmentRadios = AppointmentDate.getValidAmPmEveOptions(new Date());
  return { appointmentTime, appointmentDate, appointmentRadios, emergency: false }
};

const setEarliestAppointmentDateTimeInFuture = (appointmentDate: Date) => {
  appointmentDate = AppointmentDate.changeToValidTimeForDate(appointmentDate, "am");
  const appointmentRadios = AppointmentDate.getValidAmPmEveOptions(appointmentDate);
  return { appointmentTime: "am" as AppointmentTime, appointmentDate, appointmentRadios, emergency: false }
};

const setAppointmentDateInFuture = (appointmentDate: Date, appointmentTime: AppointmentTime) => {
  appointmentDate = AppointmentDate.changeToValidTimeForDate(appointmentDate, appointmentTime);
  const appointmentRadios = AppointmentDate.getValidAmPmEveOptions(appointmentDate);
  return { appointmentTime, appointmentDate, appointmentRadios, emergency: false }
};

const setEarliestAppointmentDateTimeForEmergency = () => {
  const appointmentTime = AppointmentDate.getEarliestAppointmentTimeForEmergency(new Date());
  console.log(appointmentTime);
  const appointmentDate = AppointmentDate.changeToValidTimeForDate(new Date(), appointmentTime, true);
  const appointmentRadios = AppointmentDate.getValidAmPmEveOptionsForEmergency();
  return { appointmentTime, appointmentDate, appointmentRadios, emergency: true }
};

export const setAppointmentDate = (appointmentDate: Date) => ({
  type: ACTIONS.SET_APPOINTMENT_DATE,
  payload: appointmentDate
})

export const setAppointmentTime = (appointmentTime: AppointmentTime) => ({
  type: ACTIONS.SET_APPOINTMENT_TIME,
  payload: appointmentTime
})

export const setAppointmentRadios = (appointmentRadios: { am: boolean, pm: boolean, eve: boolean }) => ({
  type: ACTIONS.SET_APPOINTMENT_RADIOS,
  payload: appointmentRadios
})

export const setEmergency = (emergency: boolean) => ({
  type: ACTIONS.SET_EMERGENCY,
  payload: emergency
})

export const setAll = ({ appointmentTime, appointmentDate, appointmentRadios, emergency }: { appointmentTime: AppointmentTime, appointmentDate: Date, appointmentRadios: { am: boolean, pm: boolean, eve: boolean }, emergency: boolean }) => ({
  type: ACTIONS.SET_ALL,
  payload: { appointmentTime, appointmentDate, appointmentRadios, emergency }
})

// handle date change
export const handleDateChange = (date: Dayjs) => {
  // if date hour is 0, set to 6am
  if (date.hour() === 0) {
    date.hour(6);
  }

  const isToday = AppointmentDate.checkAppointmentDateIsToday(date.toDate());
  if (isToday) {
    const result = setEarliestAppointmentTimeDateForToday();
    state.Emergency = result.emergency;
    ui.AppointmentDate = result.appointmentDate;
    ui.AppointmentTime = result.appointmentTime;
    ui.AppointmentRadios = result.appointmentRadios;
    return result;
  } else {
    const result = setEarliestAppointmentDateTimeInFuture(date.toDate());
    state.Emergency = result.emergency;
    ui.AppointmentDate = result.appointmentDate;
    ui.AppointmentTime = result.appointmentTime;
    ui.AppointmentRadios = result.appointmentRadios;
    return result;
  }
};

// handle radio change
export const handleRadioChange = (appointmentTime: AppointmentTime, appointmentDate: Date): Partial<DateSelectorState> => {
  const isToday = AppointmentDate.checkAppointmentDateIsToday(appointmentDate);
  if (isToday) {
    const result = setAppointmentDateForToday(appointmentTime);
    state.Emergency = result.emergency;
    ui.AppointmentDate = result.appointmentDate;
    ui.AppointmentTime = result.appointmentTime;
    ui.AppointmentRadios = result.appointmentRadios;
    return result;
  } else {
    const result = setAppointmentDateInFuture(appointmentDate, appointmentTime);
    state.Emergency = result.emergency;
    ui.AppointmentDate = result.appointmentDate;
    ui.AppointmentTime = result.appointmentTime;
    ui.AppointmentRadios = result.appointmentRadios;
    return result;
  }
};

// handle emergency
export const handleEmergency = () => {
  const result = setEarliestAppointmentDateTimeForEmergency();
  state.Emergency = result.emergency;
  ui.AppointmentDate = result.appointmentDate;
  ui.AppointmentTime = result.appointmentTime;
  ui.AppointmentRadios = result.appointmentRadios;
  return result;
};

export const DateSelectorContext = React.createContext<{
  state(): DateSelectorState;
  dispatch: React.Dispatch<DateSelectorActions>;
}>({ state: () => initialState(), dispatch: () => undefined });
